import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID } from '@angular/core';

import {
  applyDropdownOptionTooltip,
  resolveDropdownOptionTooltip,
} from '../utils/dropdown-option-tooltip.util';

/**
 * Gives every dropdown option in the app a hover tooltip carrying its complete
 * name, so a label too long for the popup's width is still readable.
 *
 * WHY a single document-level listener rather than an `itemTemplate` per control:
 * Syncfusion popups are portalled to `<body>`, outside the Angular component
 * tree, so no directive can reach them. A delegated listener covers EVERY
 * dropdown at once — the shared filter controls (`app-filter-select`,
 * `app-filter-multiselect`, `app-filter-tree-multiselect`) *and* the raw
 * `ejs-dropdownlist` / `ejs-multiselect` / `ejs-dropdowntree` controls inside
 * the form and modal pages — without touching a single template, so it cannot
 * regress the popup's existing markup or SCSS.
 *
 * WHY `mouseover` rather than a `MutationObserver`: the title is only needed at
 * the moment of hover, and the browser reads the attribute when its own tooltip
 * delay elapses — long after this handler has run. So the work is lazy (one
 * `closest()` per row entered) instead of a permanently-attached observer
 * churning through every grid re-render.
 */
@Injectable({ providedIn: 'root' })
export class DropdownOptionTooltipService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);

  /** Guards against a second `install()` double-binding the listener. */
  private installed = false;

  /**
   * Binds the delegated hover handler. Idempotent, and a no-op on the server
   * (there is no pointer during the SSR pass, and `document.addEventListener`
   * on the server-side DOM would leak into the next render).
   */
  install(): void {
    if (!isPlatformBrowser(this.platformId) || this.installed) {
      return;
    }
    this.installed = true;

    // Capture phase so the title lands even if a popup handler stops the event
    // from bubbling. Passive: this listener never calls `preventDefault`.
    this.document.addEventListener('mouseover', this.onMouseOver, { capture: true, passive: true });
    this.destroyRef.onDestroy(() => {
      this.document.removeEventListener('mouseover', this.onMouseOver, { capture: true });
      this.installed = false;
    });
  }

  /**
   * Arrow property so `this` survives being handed to `addEventListener`.
   *
   * `relatedTarget` is forwarded so a pointer travelling between the children of ONE option
   * row — which re-fires `mouseover` on every internal boundary — resolves that row once
   * rather than on each hop; see `resolveDropdownOptionTooltip` for why the check lives there
   * (it needs the option selector, and it must run after the target's own row is found so the
   * far more common non-dropdown hover keeps costing a single `closest()`).
   */
  private readonly onMouseOver = (event: MouseEvent): void => {
    const target = event.target;
    // Text nodes and the document itself never match an option row.
    if (!(target instanceof Element)) {
      return;
    }
    const resolved = resolveDropdownOptionTooltip(target, event.relatedTarget);
    if (resolved) {
      applyDropdownOptionTooltip(resolved);
    }
  };
}
